import { useLayoutEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Logo } from "@/components/brand/Logo";
import { useTheme, Version } from "@jbnl/shared-ui";
import {
  groepLabel,
  groups,
  handleiding,
  overzicht,
  paginaLabel,
} from "@/components/navigation";
import { useSignOut } from "@/features/session/useSignOut";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";

function submenuAriaLabel(
  t: TFunction<["navigatie", "common"]>,
  sectie: string,
  open: boolean,
) {
  return open ? t("submenu.dicht", { sectie }) : t("submenu.open", { sectie });
}

/**
 * Open while you are inside the section, and kept out of the URL: folding is chrome, not
 * something to mail in a link. The chevron is its own button, because the parent is a page too.
 */
function SubMenu({
  to,
  items,
  label,
  pathname,
}: {
  to: string;
  items: readonly { readonly to: string; readonly key: string }[];
  label: string;
  pathname: string;
}) {
  const { t } = useTranslation(["navigatie", "common"]);

  // The section's own page counts as being inside it, not just the three below it: arriving on
  // the overview is arriving at the section, and finding its pages still folded away is the
  // one moment they are certainly wanted.
  const binnen = pathname === to || items.some((sub) => pathname === sub.to);

  const [open, setOpen] = useState(binnen);
  const [vorigeBinnen, setVorigeBinnen] = useState(binnen);

  // Entering the section opens it; being in it does not hold it open. React's own way to adjust
  // state from a changed input - during render, comparing against the previous value - rather
  // than an effect, which would be a second render and is what the lint rule forbids. The
  // simpler `open={open || binnen}` was worse than it looked: it made the fold impossible on
  // exactly the pages where someone would want it.
  if (binnen !== vorigeBinnen) {
    setVorigeBinnen(binnen);
    if (binnen) setOpen(true);
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <SidebarMenuAction
          aria-label={submenuAriaLabel(t, label, open)}
          className="data-[state=open]:rotate-90"
          data-state={open ? "open" : "closed"}
        >
          <ChevronRight className="transition-transform" />
        </SidebarMenuAction>
      </CollapsibleTrigger>

      {/* Radix publishes the measured height; `overflow-hidden` keeps rows in while it animates. */}
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <SidebarMenuSub>
          {items.map((sub) => (
            <SidebarMenuSubItem key={sub.to}>
              <SidebarMenuSubButton
                asChild
                isActive={pathname === sub.to}
                className="data-[active=true]:bg-secondary-surface data-[active=true]:font-bold data-[active=true]:text-secondary-active"
              >
                <Link to={sub.to}>
                  <span>{paginaLabel(t, sub.key)}</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** One of the two pinned entries above the groups - its own component so the tree under `Sidebar` stays shallow. */
function TopNavItem({
  to,
  icon: Icon,
  label,
  pathname,
}: {
  to: string;
  icon: typeof overzicht.icon;
  label: string;
  pathname: string;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={pathname === to}
        tooltip={label}
        className="data-[active=true]:bg-secondary-surface data-[active=true]:font-bold data-[active=true]:text-secondary-active"
      >
        <Link to={to}>
          <Icon />
          <span>{label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** One grouped entry, with its optional SubMenu - its own component for the same reason as TopNavItem. */
function GroupNavItem({
  item,
  pathname,
}: {
  item: (typeof groups)[number]["items"][number];
  pathname: string;
}) {
  const { t } = useTranslation(["navigatie", "common"]);

  return (
    <SidebarMenuItem>
      {/* Selected is blue, not the default grey: blue is the huisstijl's
          systemic colour and red is reserved for actions. */}
      <SidebarMenuButton
        asChild
        isActive={pathname === item.to}
        tooltip={paginaLabel(t, item.key)}
        className="data-[active=true]:bg-secondary-surface data-[active=true]:font-bold data-[active=true]:text-secondary-active"
      >
        <Link to={item.to}>
          <item.icon />
          <span>{paginaLabel(t, item.key)}</span>
        </Link>
      </SidebarMenuButton>

      {"items" in item && (
        <SubMenu
          to={item.to}
          items={item.items}
          label={paginaLabel(t, item.key)}
          pathname={pathname}
        />
      )}
    </SidebarMenuItem>
  );
}

/**
 * Where the menu was scrolled to, kept outside React on purpose.
 *
 * Every page renders its own `Shell`, so a navigation unmounts this whole subtree and mounts a new
 * one - a fresh DOM node, scrolled back to the top, taking the entry just clicked out from under
 * the cursor. Hoisting the Shell into a layout route would fix the cause and touches every screen;
 * this restores the position across the remount.
 */
let menuScrollTop = 0;

export function AppSidebar({ name }: { name: string }) {
  const menu = useRef<HTMLDivElement>(null);
  const touched = useRef(false);

  // Layout, not effect: restoring after paint is a visible jump from the top. The menu can be
  // shorter on the first frame (a folded section is still folded), which clamps the restored value
  // to a smaller one, so it is applied again whenever the content grows until the user scrolls.
  useLayoutEffect(() => {
    const el = menu.current;
    if (!el) return;
    el.scrollTop = menuScrollTop;
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (!touched.current) el.scrollTop = menuScrollTop;
    });
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, []);

  const { t } = useTranslation(["navigatie", "common"]);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const signOut = useSignOut();
  const theme = useTheme();

  return (
    <Sidebar>
      {/* Logo over a thin Ocean Blue rule - the letterhead lockup, which is where JBNL puts
          the mark on every surface it owns. */}
      <SidebarHeader className="gap-0 border-b border-brand-blue px-4 py-4">
        <Link to="/" className="flex items-center gap-3">
          <Logo className="h-10" />
          <span className="flex flex-col leading-tight">
            <span className="font-bold tracking-[-0.02em]">
              {t("common:shell.product")}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("common:shell.portal")}
            </span>
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent
        ref={menu}
        onPointerDown={() => (touched.current = true)}
        onWheel={() => (touched.current = true)}
        onTouchStart={() => (touched.current = true)}
        onKeyDown={() => (touched.current = true)}
        onScroll={(event) => {
          // A scroll the browser clamped while the menu was still growing is not the user's.
          if (touched.current || event.currentTarget.scrollTop >= menuScrollTop) {
            menuScrollTop = event.currentTarget.scrollTop;
          }
        }}
      >
        {/* Two literal items rather than a loop: Overzicht's label lives under its own catalog
            key, so a loop would need that exception anyway. */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <TopNavItem
                to={overzicht.to}
                icon={overzicht.icon}
                label={t("overzicht.label")}
                pathname={pathname}
              />
              <TopNavItem
                to={handleiding.to}
                icon={handleiding.icon}
                label={paginaLabel(t, handleiding.key)}
                pathname={pathname}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {groups.map((group) => (
          <SidebarGroup key={group.key}>
            <SidebarGroupLabel className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
              {groepLabel(t, group.key)}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <GroupNavItem key={item.to} item={item} pathname={pathname} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="gap-2 border-t p-2 group-data-[collapsible=icon]:hidden">
        <div className="px-2 pt-2">
          <span className="block text-sm font-medium">{name}</span>
          <span className="block text-xs text-muted-foreground">
            {t("common:shell.rol")}
          </span>
        </div>
        {/* Uitloggen sits under the name rather than in a menu behind it: it is the only thing
            you can do to your own session, and the wrong-account case is why you look here. */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={theme.cycle} tooltip={theme.label}>
              <theme.Icon />
              <span>{theme.label}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
              tooltip={t("common:shell.signOut")}
            >
              <LogOut />
              <span>
                {signOut.isPending
                  ? t("common:actions.busy")
                  : t("common:shell.signOut")}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Last line of the menu, under Uitloggen. Hidden with the rest of the footer when the
            sidebar is collapsed to icons - there is no room, and nobody collapses a sidebar to
            read a version number. */}
        <Version className="px-2" />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
